import { folioCreditoLabels, isF29Movement } from '../data'

export const getBankMovementType = item => {
  const explicit = String(item?.categoriaTipoMovimiento ?? item?.tipoMovimiento ?? '').trim().toLowerCase()
  if (['abono', 'cargo'].includes(explicit)) return explicit
  const table = [item?.tabla, item?.tablaPrincipal, item?.origenTabla, item?.libroContableOriginTableId]
    .map(Number).find(value => [6, 7].includes(value))
  if (table != null) return table === 6 ? 'abono' : 'cargo'
  if (Math.abs(Number(item?.montoCargo)) > 0) return 'cargo'
  if (Math.abs(Number(item?.montoAbono)) > 0) return 'abono'
  return null
}

export const isOppositeBankMovement = (movement, target) => {
  const targetType = getBankMovementType(target)
  return targetType != null && getBankMovementType(movement) === (targetType === 'abono' ? 'cargo' : 'abono')
}

export const isBankConciliationMovement = item =>
  [item?.tabla, item?.tablaPrincipal, item?.origenTabla, item?.libroContableOriginTableId]
    .some(value => [6, 7].includes(Number(value)))

export const mergeDocumentConciliationLinkage = (rows, linkages) =>
  mergeConciliationLinkage(rows, linkages).filter(item =>
    !isBankConciliationMovement(item) &&
    !linkages.some(linkage => isBankConciliationMovement(linkage) && isSameConciliationMovement(item, linkage))
  )

export const hasFolioCredito = item =>
  item?.folioCredito !== null &&
  item?.folioCredito !== undefined &&
  item?.folioCredito !== ''

export const hasKnownVinculacionFolioCredito = item =>
  !isF29Movement(item) &&
  hasFolioCredito(item) &&
  Object.prototype.hasOwnProperty.call(folioCreditoLabels, Number(item.folioCredito))

export const isVinculacionRow = item => {
  if (isF29Movement(item)) return false
  const hasManualOrigin = [item?.nombreOrigen, item?.origen].some(
    value => String(value || '').trim().toLowerCase() === 'manual'
  )

  return (
    hasKnownVinculacionFolioCredito(item) ||
    hasManualOrigin ||
    item?._isDinRevVirtual === true
  )
}

export const isSiiDocument = item => {
  if (isBankConciliationMovement(item) || isVinculacionRow(item) || isF29Movement(item)) return false
  const table = Number(item?.origenTabla ?? item?.tablaPrincipal ?? item?.tabla)
  if ([4, 11, 29, 30, 39].includes(table)) return false
  const origin = String(item?.nombreOrigen ?? item?.origen ?? '').trim().toLowerCase()
  return origin ? origin === 'sii' : [2, 8, 27].includes(table)
}

export const getMovementFolio = item =>
  [item?.numeroFolio, item?.folio]
    .map(value => String(value ?? '').trim())
    .find(Boolean) || ''

export const isBoletaHonorarios = item =>
  [item?.nombreFolio, item?.nombreCuenta]
    .some(value => {
      const name = String(value || '').trim().toLowerCase()
      // "Boleta" es el nombre legacy de honorarios; las boletas electrónicas
      // de ventas y sus resúmenes deben conservar el monto total con IVA.
      return name === 'boleta' || /^boletas?\s+(?:de\s+)?honorarios\b/.test(name)
    })

export const getMovementDisplayAmount = item => {
  const candidates = isBoletaHonorarios(item)
    ? [item?.montoNetoLiquido, item?.montoNeto, item?.montoTotal, item?.monto]
    : [
        item?.montoTotal,
        item?.monto,
        item?.amount,
        item?.montoRelacionado,
        item?.montoAbono,
        item?.montoCargo
      ]
  const amount = candidates.find(value => {
    if (value === null || value === undefined || value === '') return false
    return Number.isFinite(Number(value))
  })

  return Math.abs(Number(amount ?? 0))
}

export const getMovementIdentityKeys = item => {
  const vinculacionFolio = getMovementFolio(item)
  const semanticKey =
    hasKnownVinculacionFolioCredito(item) && vinculacionFolio
      ? `vinculacion:${String(item.folioCredito)}:${vinculacionFolio}`
      : null

  return [...new Set([item?.idPrincipal, item?.id, item?.customId, semanticKey]
    .filter(value => value !== null && value !== undefined && value !== '')
    .map(String))]
}

export const mergeUniqueMovements = values => {
  const seen = new Set()

  return values.filter(item => {
    const keys = getMovementIdentityKeys(item)
    if (keys.length === 0 || keys.some(key => seen.has(key))) return false
    keys.forEach(key => seen.add(key))
    return true
  })
}

// El comprobante indica un tipo y puede repetirse en documentos distintos.
export const isSameConciliationMovement = (item, linkage) => {
  const keys = getMovementIdentityKeys(item)
  return getMovementIdentityKeys(linkage).some(key => keys.includes(key))
}

export const getMovementRelationDate = (item, linkages = []) => {
  const linked = linkages.find(link => isSameConciliationMovement(item, link))
  return linked ? linked.fechaRelacionada : item?.fecha
}

export const getMovementAssignedAmount = (item, linkages) => {
  const linkage = (linkages ?? []).find(link => isSameConciliationMovement(item, link))
  // El monto del linkage es lo aplicado a este movimiento bancario, no el
  // total del documento ni su saldo pendiente (auxiliar).
  if (linkage?.monto != null) return Math.abs(Number(linkage.monto))
  return [29, 30].includes(Number(item.origenTabla))
    ? item.auxiliar ?? 0
    : item.montoRelacionado ?? item.auxiliar ?? 0
}

export const getMovementAppliedAmount = (item, linkages) => {
  const linkage = (linkages ?? []).find(link => isSameConciliationMovement(item, link))
  return linkage?.monto != null
    ? Math.abs(Number(linkage.monto))
    : getMovementDisplayAmount(item)
}

export const getMovementPaymentStatus = (item, bankMovement) => {
  const ownStatus = String(item?.status || '').replace(/\s*undefined\s*/gi, '').trim().toUpperCase()
  const fallback = ownStatus === '-' ? '' : ownStatus
  const linkage = (bankMovement?.linkage ?? []).find(link => isSameConciliationMovement(item, link))
  const total = getMovementDisplayAmount(item)
  const assigned = Math.abs(Number(linkage?.monto))
  const type = String(bankMovement?.categoriaTipoMovimiento || bankMovement?.tipoMovimiento || '').toLowerCase()
  const paidStatus = type === 'cargo' ? 'PAGADO' : type === 'abono' ? 'COBRADO' : ''

  if (!linkage || !paidStatus || total <= 0 || !Number.isFinite(assigned)) return fallback
  if (assigned >= total) return paidStatus
  // El estado propio puede incluir pagos realizados con otros movimientos.
  if (fallback) return fallback
  return assigned > 0 ? `PARCIALMENTE ${paidStatus}` : type === 'cargo' ? 'POR PAGAR' : 'POR COBRAR'
}

export const mergeConciliationLinkage = (rows, linkages) => {
  const result = mergeUniqueMovements([...rows])
  linkages.forEach(linkage => {
    const index = result.findIndex(item => isSameConciliationMovement(item, linkage))
    if (index >= 0) {
      result[index] = {
        ...result[index],
        // El total pertenece al documento; el vínculo puede traer el líquido.
        montoTotal: result[index].montoTotal ?? linkage.montoTotal,
        monto: linkage.monto ?? 0,
        seleccionado: true
      }
    } else {
      result.push({ ...linkage, id: linkage.id ?? linkage.idPrincipal, seleccionado: true })
    }
  })
  return result
}
